import { type MouseEvent, type MutableRefObject, memo, type ReactNode, useCallback, useRef } from 'react';

import { XIcon } from '@air/design-icons';
import { Popover, type PopoverProps } from '@air/primitive-popover';
import { Tooltip } from '@air/primitive-tooltip';

import { workspaceAgentComposerPillRemoveButton } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerChrome';
import {
  workspaceAgentPanelPill,
  workspaceAgentPanelPillInteraction,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelPillChrome';
import { WORKSPACE_AGENT_PILL_TRIGGER_ATTRIBUTE } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPillTriggerAttribute';
import { preserveWorkspaceAgentComposerFocus } from '~/components/v2/components/workspace/workspace-agent-panel/utils/preserveWorkspaceAgentComposerFocus';

const preventCloseAutoFocus = (event: Event) => {
  event.preventDefault();
};

/**
 * A press that only closed a card must not reach the page behind the panel. Page-level Escape hotkeys bind
 * the document in the bubble phase (`react-hotkeys-hook`), and the keydown target is the card's close button
 * rather than the editor, so guards that exempt a contenteditable never catch it — the board page's
 * `MultiselectKeys`, which would clear the selection the composer's scope pills read, is the example. Radix
 * calls this from a document *capture* listener, so stopping here ends the dispatch before any bubble-phase
 * listener sees the press. No `preventDefault`: Radix must still dismiss the card.
 */
const stopEscapeKeyPropagation = (event: KeyboardEvent) => {
  event.stopPropagation();
};

type WorkspaceAgentPillPointerDownOutsideEvent = Parameters<NonNullable<PopoverProps['onPointerDownOutside']>>[0];

type WorkspaceAgentReferencePillSharedProps = {
  /** Cover or thumbnail, else the subject's glyph. */
  leadingVisual: ReactNode;
  title: string;
  triggerTestId: string;
};

type WorkspaceAgentReferencePillCardProps = {
  /** The popover body, built by the caller: the pill knows nothing about the subject. */
  card: Exclude<ReactNode, null | undefined>;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

type WorkspaceAgentReferencePillHistoryProps = WorkspaceAgentReferencePillCardProps & {
  /** Radix refocuses the trigger on close; set while the caller is handing focus somewhere else. */
  shouldPreventCloseAutoFocus?: boolean;
  surface: 'history';
  testId: string;
  /** Required here: only this surface puts focus back on the chip before it navigates. */
  triggerRef: MutableRefObject<HTMLButtonElement | null>;
  /** Runs on trigger press before Radix toggles the preview (e.g. copy-on-click for share links). */
  onTriggerClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  triggerAriaLabel?: string;
  triggerClassName?: string;
};

type WorkspaceAgentReferencePillComposerBaseProps = {
  isSelected: boolean;
  /** Prefixes the accessible name, and stands in for a label that never resolved: a chip is an island in prose. */
  kind: 'Asset' | 'Board' | 'Brand kit image';
  onRemove: () => void;
  surface: 'composer';
};

type WorkspaceAgentReferencePillComposerWithCardProps = WorkspaceAgentReferencePillComposerBaseProps &
  WorkspaceAgentReferencePillCardProps & {
    /**
     * Required: the pill suppresses Radix's close autofocus, so the caller owns where focus lands. Called
     * for a press or a Tab the user aimed outside the card, both of which have already chosen a destination.
     */
    onOutsideDismiss: () => void;
    /**
     * Required: Radix's close autofocus is the one hook every close reaches — an open change misses
     * the closes the caller makes itself — so this surface takes it over and the caller decides from
     * there where focus goes.
     */
    restoreFocusAfterClose: () => void;
  };

type WorkspaceAgentReferencePillComposerWithoutCardProps = WorkspaceAgentReferencePillComposerBaseProps & {
  card: null;
};

export type WorkspaceAgentReferencePillProps = WorkspaceAgentReferencePillSharedProps &
  (
    | WorkspaceAgentReferencePillHistoryProps
    | WorkspaceAgentReferencePillComposerWithCardProps
    | WorkspaceAgentReferencePillComposerWithoutCardProps
  );

/** Opens a preview card rather than navigating: the card confirms which subject the agent meant. */
export const WorkspaceAgentReferencePill = memo((props: WorkspaceAgentReferencePillProps) => {
  const { leadingVisual, title, triggerTestId } = props;
  const composer = props.surface === 'composer' ? props : undefined;
  const hasCard = props.card !== null;
  const card = hasCard ? props.card : null;
  const isOpen = hasCard ? props.isOpen : false;
  const onOpenChange = hasCard ? props.onOpenChange : undefined;
  const historyTestId = props.surface === 'history' ? props.testId : undefined;
  const history = props.surface === 'history' ? props : undefined;
  const historyCloseAutoFocus =
    props.surface === 'history' && props.shouldPreventCloseAutoFocus === true ? preventCloseAutoFocus : undefined;
  const onTriggerClick = history?.onTriggerClick;
  const triggerAriaLabel = history?.triggerAriaLabel;
  const triggerClassName = history?.triggerClassName;
  const onRemove = composer?.onRemove;
  const composerWithCard = props.surface === 'composer' && props.card !== null ? props : undefined;
  const restoreFocusAfterClose = composerWithCard?.restoreFocusAfterClose;
  const onOutsideDismiss = composerWithCard?.onOutsideDismiss;
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const callerTriggerRef = props.surface === 'history' ? props.triggerRef : undefined;

  /** Two refs on one node: the pill needs its own for the press check, the caller its own for focus. */
  const setTriggerNode = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;

      if (callerTriggerRef) {
        callerTriggerRef.current = node;
      }
    },
    [callerTriggerRef],
  );

  /**
   * Radix dispatches this before it decides the dismissal, then cancels it outright when the press landed
   * on the trigger — the trigger's own click closes the card instead, a close the caller still owns. So a
   * press there must not be read as aimed elsewhere, or that close would hand focus to nothing.
   */
  const handlePointerDownOutside = useCallback(
    (event: WorkspaceAgentPillPointerDownOutsideEvent) => {
      const target = event.detail.originalEvent.target;

      if (target instanceof Node && triggerRef.current?.contains(target)) {
        return;
      }

      onOutsideDismiss?.();
    },
    [onOutsideDismiss],
  );

  /**
   * Radix would otherwise refocus the trigger, which on this surface is a `contentEditable={false}`
   * island the node view swallows keydown inside — a dead end for the keyboard. Every close runs through
   * here, including the ones the caller made itself, so it is also where the caret gets handed back.
   */
  const handleComposerCloseAutoFocus = useCallback(
    (event: Event) => {
      event.preventDefault();
      restoreFocusAfterClose?.();
    },
    [restoreFocusAfterClose],
  );

  const handleRemove = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      onRemove?.();
    },
    [onRemove],
  );

  const handleHistoryTriggerClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      onTriggerClick?.(event);
    },
    [onTriggerClick],
  );

  const displayTitle = composer ? title || composer.kind : title;
  /**
   * Composer only, and the kind alone when the title never resolved: "Asset: Asset" says it twice. A
   * history chip names itself with its visible title, which `triggerTooltip` already describes.
   */
  const composerAccessibleName = composer && (title ? `${composer.kind}: ${title}` : composer.kind);

  const body = (
    <>
      {leadingVisual}
      <span
        className={composer ? 'min-w-0 truncate' : 'truncate whitespace-nowrap'}
        data-testid={composer ? 'WORKSPACE_AGENT_PILL_LABEL' : undefined}
      >
        {displayTitle}
      </span>
    </>
  );

  const popover = (
    <Popover
      // Not the bare title: the chip already carries that, so the card would announce as its twin.
      aria-label={`${displayTitle} preview`}
      // The card's media bleeds to its own edge, so the popover cannot keep its padding or width floor.
      // `rounded-8` matches the card frame: at the primitive's `rounded-4` the frame's corners exposed
      // the popover surface behind them. The cap is the available height exactly — the primitive's
      // flat 32px of slack clipped cards that fit — and it scrolls rather than hiding the overflow,
      // which `overflow-hidden` here would otherwise take from the primitive and cut the meta row off.
      className="max-h-[var(--radix-popover-content-available-height)] w-auto min-w-0 overflow-y-auto overflow-x-hidden rounded-8 p-0"
      onCloseAutoFocus={composer ? handleComposerCloseAutoFocus : historyCloseAutoFocus}
      onEscapeKeyDown={stopEscapeKeyPropagation}
      onFocusOutside={onOutsideDismiss}
      onOpenChange={onOpenChange}
      onPointerDownOutside={composer ? handlePointerDownOutside : undefined}
      open={isOpen}
      side="top"
      size="large"
      trigger={
        // The popover clones its handlers onto this element, so it has to be the DOM node itself.
        <button
          // The composer keymap presses this button for an Enter or a Space on the node-selected chip;
          // the remove button beside it carries no marker.
          {...(composer ? { [WORKSPACE_AGENT_PILL_TRIGGER_ATTRIBUTE]: '' } : undefined)}
          aria-expanded={composer ? undefined : isOpen}
          aria-haspopup={composer ? undefined : 'dialog'}
          aria-label={composer ? composerAccessibleName || undefined : triggerAriaLabel}
          className={
            composer
              ? workspaceAgentPanelPillInteraction({
                  // Inset ring: the trigger fills the chip, so an outset ring would be clipped by it.
                  className: 'flex h-full min-w-0 items-center gap-1 pr-1 focus-visible:ring-inset',
                })
              : workspaceAgentPanelPill({
                  className: triggerClassName ? `min-w-0 ${triggerClassName}` : 'min-w-0',
                  hasLeadingThumbnail: true,
                  isInteractive: true,
                  isOpen,
                })
          }
          data-testid={triggerTestId}
          onClick={onTriggerClick ? handleHistoryTriggerClick : undefined}
          onMouseDown={composer ? preserveWorkspaceAgentComposerFocus : undefined}
          // Radix's `asChild` trigger composes its own ref with this one, so both still reach the button.
          ref={setTriggerNode}
          // A composer chip sits in a `contentEditable={false}` island, so a tab stop here is a trap.
          tabIndex={composer ? -1 : undefined}
          type="button"
        >
          {body}
        </button>
      }
      // On the button, not the label: the truncated span is not the hover target the pointer finds.
      triggerTooltip={displayTitle}
    >
      {card}
    </Popover>
  );

  if (!composer) {
    return (
      <span className="inline-flex max-w-full items-center align-middle leading-none" data-testid={historyTestId}>
        {popover}
      </span>
    );
  }

  return (
    <span
      className={workspaceAgentPanelPill({
        // The leading block is drawn either way, so the chip never carries the glyph inset.
        hasLeadingThumbnail: true,
        isOpen,
        isSelected: composer.isSelected,
        surface: 'composer',
        tone: 'mention',
      })}
    >
      {card ? (
        popover
      ) : (
        // No card: a button that opens nothing would still take focus and announce as a dialog trigger.
        <Tooltip label={displayTitle}>
          <span className="flex h-full min-w-0 items-center gap-1 pr-1">
            {/* The kind the other branch keeps in the trigger's `aria-label`: with no button there is no
                label to hold it, and the chip would read as a bare title in the middle of prose. Dropped
                when the title never resolved, since the kind is then already the visible text. */}
            {title ? <span className="sr-only">{`${composer.kind}: `}</span> : null}
            {body}
          </span>
        </Tooltip>
      )}
      {/* A plain button, not `IconButton`: the chip's square overrides its size, radius and every colour. */}
      <button
        aria-label={`Remove ${displayTitle}`}
        className={workspaceAgentComposerPillRemoveButton()}
        data-testid="WORKSPACE_AGENT_PILL_REMOVE"
        onClick={handleRemove}
        onMouseDown={preserveWorkspaceAgentComposerFocus}
        // Out of the tab order; Backspace removes the whole atom.
        tabIndex={-1}
        type="button"
      >
        <XIcon size="xs" />
      </button>
    </span>
  );
});

WorkspaceAgentReferencePill.displayName = 'WorkspaceAgentReferencePill';
